import { allowAuthenticationOrigin } from '../data/workspace'
import { getSupabaseConfig } from '../supabase/config'
import { indexedDbAuthStorage, nativeOAuthAttemptStorage } from '../supabase/authStorage'
import { getNativePlatform } from '../utils/nativeRuntime'
import { createAuthApi, type AuthApi } from './authApi'
import { NeonAuthProvider, type SessionStore } from './NeonAuthProvider'
import { neonSessionKey, parseStoredSession, serializeSession } from './session'
import { createTokenManager, type TokenManager } from './tokenManager'

export interface NeonRuntime {
  environment: 'development' | 'production'
  dataApiUrl: string
  api: AuthApi
  tokens: TokenManager
  sessions: SessionStore
  provider: NeonAuthProvider
}

let runtime: NeonRuntime | null = null

/** The one auth lifecycle of this document: the provider and the data client share a session store and a token manager. Null unless VITE_BACKEND=neon is configured. */
export function getNeonRuntime(): NeonRuntime | null {
  if (runtime) return runtime
  const config = getSupabaseConfig()
  if (!config || config.backend !== 'neon' || !config.authUrl) return null
  // The sign-in return is redeemed while the workspace is still opening, so the guard on every request must let the auth Worker through.
  allowAuthenticationOrigin(new URL(config.authUrl).origin)
  const api = createAuthApi(config.authUrl)
  const key = neonSessionKey(config.environment)
  // Written through indexedDbAuthStorage on purpose: it is what tells the workspace layer who is signed in.
  const sessions: SessionStore = {
    read: async () => parseStoredSession(await indexedDbAuthStorage.getItem(key)),
    write: (session) => indexedDbAuthStorage.setItem(key, serializeSession(session)),
    clear: () => indexedDbAuthStorage.removeItem(key),
  }
  let provider: NeonAuthProvider | null = null
  const tokens = createTokenManager({
    api,
    getBearer: async () => (await sessions.read())?.bearer ?? null,
    onInvalid: () => provider?.sessionInvalidated(),
  })
  provider = new NeonAuthProvider({
    environment: config.environment,
    api,
    tokens,
    sessions,
    attempts: nativeOAuthAttemptStorage,
    native: {
      isAndroid: () => getNativePlatform(window) === 'android',
      openBrowser: async (url) => {
        const { Browser } = await import('@capacitor/browser')
        await Browser.open({ url, toolbarColor: '#080b1d' })
      },
      closeBrowser: async () => {
        const { Browser } = await import('@capacitor/browser')
        await Browser.close()
      },
    },
    web: {
      origin: () => window.location.origin,
      currentPath: () => `${window.location.pathname}${window.location.search}${window.location.hash}`,
      search: () => window.location.search,
      navigate: (url) => window.location.assign(url),
      replacePath: (path) => window.history.replaceState(null, '', path),
    },
    now: Date.now,
  })
  runtime = { environment: config.environment, dataApiUrl: config.url, api, tokens, sessions, provider }
  return runtime
}

export function resetNeonRuntimeForTests(): void {
  runtime = null
}
